import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import './Analytics.css';

// Lazy load tab components for better initial load performance
const AnalyticsOverview = lazy(() => import('./AnalyticsOverview'));
const AnalyticsTokens = lazy(() => import('./AnalyticsTokens'));
const AnalyticsSessions = lazy(() => import('./AnalyticsSessions'));
const AnalyticsConversations = lazy(() => import('./AnalyticsConversations'));
const AnalyticsCost = lazy(() => import('./AnalyticsCost'));
const AnalyticsSentiment = lazy(() => import('./AnalyticsSentiment'));

const Analytics = () => {
  const [activeAgentId, setActiveAgentId] = useState(null);
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    const agentId = localStorage.getItem('activeAgentId');
    if (agentId) {
      setActiveAgentId(agentId);
    }
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'token-analytics', label: 'Tokens' },
    { id: 'cost-analytics', label: 'Cost Breakdown' },
    { id: 'sessions', label: 'Sessions' },
    { id: 'conversations', label: 'Conversations' },
    { id: 'sentiment', label: 'Sentiment' }
  ];

  const getHeaderContent = () => {
    switch (currentTab) {
      case 'overview':
        return { title: 'Intelligence Insights', description: 'Visualizing cognitive performance and neural engagement.' };
      case 'token-analytics':
        return { title: 'Token Dynamics', description: 'Deep dive into linguistic consumption and efficiency.' };
      case 'cost-analytics':
        return { title: 'Financial Audit', description: 'Real-time expenditure tracking and model cost estimation.' };
      case 'sessions':
        return { title: 'Session Archive', description: 'Historical record of autonomous agent interactions.' };
      case 'conversations':
        return { title: 'Trace Explorer', description: '' };
      case 'sentiment':
        return { title: 'Cognitive Sentiment', description: 'Emotional resonance and user satisfaction metrics.' };
      default:
        return { title: 'Neural Analytics', description: 'Advanced agentic performance monitoring.' };
    }
  };

  const { title, description } = getHeaderContent();

  if (!activeAgentId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
        <div className="premium-spinner mb-4" />
        <div className="text-slate-400 font-bold text-xs uppercase tracking-widest">Identifying Active Agent...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20 w-full min-w-0 flex-1 overflow-x-hidden">
      <div className="w-full max-w-full min-w-0 px-6 md:px-10 py-8">
        {currentTab !== 'conversations' && (
          <PageHeader 
            title={title} 
            description={description}
            className="text-left mb-8"
          />
        )}

        {/* Tab switcher removed as requested */}

        <div className="w-full">
          <Suspense fallback={
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
              <div className="premium-spinner"></div>
              <div className="loading-text text-slate-400 font-bold text-xs uppercase tracking-widest">Initializing Module...</div>
            </div>
          }>
            {currentTab === 'overview' && <AnalyticsOverview activeAgentId={activeAgentId} />}
            {currentTab === 'token-analytics' && <AnalyticsTokens activeAgentId={activeAgentId} />}
            {currentTab === 'cost-analytics' && <AnalyticsCost activeAgentId={activeAgentId} />}
            {currentTab === 'sessions' && <AnalyticsSessions activeAgentId={activeAgentId} />}
            {currentTab === 'conversations' && <AnalyticsConversations activeAgentId={activeAgentId} />}
            {currentTab === 'sentiment' && <AnalyticsSentiment activeAgentId={activeAgentId} />}
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
